import {
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ProductService } from 'src/app/shared/services/product.service';
import { TranslateService } from '@ngx-translate/core';
import {
    AuctionStatus,
    BasicListResponse,
    GetFeaturedAuctions200ResponseAllOfDataInner,
    AuctionCatalogsService as AuctionsService,
    GetFeaturedAuctions200Response,
    GetListByGroup200Response,
    GetAuctionFiltersResponse,
    GetAuctionFiltersResponseDataAuctionTypesInner,
    GetAuctionFiltersResponseDataStatusesInner,
} from '@viewbid/ts-angular-client';
import { UtilsService } from '../../services/utils.service';
import {
    activeAuctionFilterCount,
    AuctionFilterStatus,
    AuctionFilterValue,
    AuctionTypeOption,
    DEFAULT_AUCTION_FILTER,
    isAuctionStatusInvalid,
    VbSnackbarService,
} from '@viewbid/ui';
import { getEarliestLotCloseTime } from '@/vb-utils/lot.util';
import {
    getAuctionStatusText,
    getAuctionStatus,
    getAuctionStatusBannerText,
    getCategoryClass,
} from '@/vb-utils/auction.util';
import { BiddingUtils } from '@/vb-utils/bidding.util';
import { scrollUpTo } from '@/vb-utils/scroll-up-to.util';
import { catchError, EMPTY, filter, Subject, switchMap, takeUntil } from 'rxjs';
import { DeviceCheckService } from 'src/app/viewbid/shared/services/device-check.service';
import { ViewportScroller } from '@angular/common';
import { SocketService } from 'src/app/viewbid/shared/services/socket.service';

const API_STATUS_BY_FILTER: Record<AuctionFilterStatus, AuctionStatus> = {
    upcoming: AuctionStatus.Public,
    open: AuctionStatus.Open,
    closed: AuctionStatus.Closed,
};

const FILTER_STATUS_BY_API: Partial<
    Record<AuctionStatus, AuctionFilterStatus>
> = {
    [AuctionStatus.Public]: 'upcoming',
    [AuctionStatus.Open]: 'open',
    [AuctionStatus.Closed]: 'closed',
};

function copyAuctionFilter(filter: AuctionFilterValue): AuctionFilterValue {
    return { ...filter, statuses: [...filter.statuses] };
}

@Component({
    selector: 'app-auctions-page',
    templateUrl: './auctions-page.component.html',
    styleUrls: ['./auctions-page.component.scss'],
})
export class AuctionsPageComponent implements OnInit, OnDestroy {
    public niceSelectOptions = this.productService.filterSelect;
    public paginate: any = {}; // Pagination use only

    private pageNo: number = 1;
    public pageSize: number = 10;
    public readonly pageSizeOptions: ReadonlyArray<number> = [10, 25, 50];

    private searchQuery: string | undefined = undefined;

    // Committed filter drives the list; the draft is the modal's working copy.
    // The type rides the `category` query param so back/forward restore it.
    public appliedFilter: AuctionFilterValue = copyAuctionFilter(
        DEFAULT_AUCTION_FILTER,
    );
    public filterDraft: AuctionFilterValue = copyAuctionFilter(
        DEFAULT_AUCTION_FILTER,
    );
    public filterModalOpen: boolean = false;
    public filterTypes: AuctionTypeOption[] = [];

    // Live facet counts for the modal's draft, from getAuctionFilters. They are
    // prefetched for the applied filter so the modal opens with counts in place,
    // and the previous counts stay up while a refetch is in flight. Clearing them
    // between requests made the chips and Apply label flicker on every click.
    public filterMatchCount?: number;
    public filterStatusCounts?: Partial<Record<AuctionFilterStatus, number>>;
    public filterTypeCounts?: Record<string, number>;
    private filterCounts$ = new Subject<AuctionFilterValue>();

    auctions!: GetFeaturedAuctions200ResponseAllOfDataInner[];

    activeTab = 'list';
    private destroy$ = new Subject<void>();
    public isMobile: boolean = false;
    /** Search bar above the list — the scroll target when the reader picks a page. */
    @ViewChild('searchBar', { read: ElementRef })
    private searchBar?: ElementRef<HTMLElement>;
    public isPhone: boolean = false;
    earliestLotClosingTime: string = '';

    public isLoading: boolean = true;
    public unableToLoad: boolean = false;
    public filteredAuctions: GetFeaturedAuctions200ResponseAllOfDataInner[] =
        [];
    currentUserId: string = '';

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        public productService: ProductService,
        private auctionService: AuctionsService,
        public translate: TranslateService,
        public utilService: UtilsService,
        private snackbarService: VbSnackbarService,
        protected biddingUtils: BiddingUtils,
        private deviceCheckService: DeviceCheckService,
        private viewportScroller: ViewportScroller,
        private socketService: SocketService,
    ) {
        this.pageNo = Number(this.route.snapshot.queryParams['page']) || 1;
        this.appliedFilter.type =
            this.route.snapshot.queryParams['category'] ?? '';
        this.getAllAuctionsServiceCall(this.pageNo);
    }

    ngOnInit(): void {
        this.deviceCheckService.isMobile$
            .pipe(takeUntil(this.destroy$))
            .subscribe((isMobile: boolean) => {
                this.isMobile = isMobile;
            });

        // Phone widths render the card view automatically — an auto-triggered
        // view (toggle hidden), NOT a rewrite of the user's grid/list choice,
        // which is restored verbatim when the viewport widens again.
        this.deviceCheckService.isPhone$
            .pipe(takeUntil(this.destroy$))
            .subscribe((isPhone: boolean) => {
                this.isPhone = isPhone;
            });

        // React to URL changes (e.g. browser back/forward) — guarded so
        // getPager() updating the URL can't re-trigger a load.
        this.route.queryParams
            .pipe(takeUntil(this.destroy$))
            .subscribe((queryParams: Params) => {
                const type: string = queryParams['category'] ?? '';
                if (type !== this.appliedFilter.type) {
                    this.appliedFilter = { ...this.appliedFilter, type };
                    this.pageNo = 1;
                    this.getAllAuctionsServiceCall(1);
                    return;
                }
                const page: number = Number(queryParams['page']) || 1;
                if (page !== this.pageNo) {
                    this.pageNo = page;
                    this.getAllAuctionsServiceCall(page);
                }
            });

        this.auctionService
            .getListByGroup('categories')
            .pipe(takeUntil(this.destroy$))
            .subscribe((res: GetListByGroup200Response) => {
                this.filterTypes = res.data
                    .filter(({ basicKey }: BasicListResponse) => basicKey)
                    .map(({ value, basicKey }: BasicListResponse) => ({
                        value: basicKey,
                        label: this.capitalizeFirst(value),
                    }));
                // Counts seed a zero for every type, so fetch once types exist.
                this.filterCounts$.next(this.appliedFilter);
            });

        // switchMap drops a stale response when the draft changes mid-flight.
        // An empty status selection can't be applied, so it isn't counted.
        this.filterCounts$
            .pipe(
                filter((draft: AuctionFilterValue) => {
                    if (!isAuctionStatusInvalid(draft)) return true;
                    this.filterMatchCount = undefined;
                    return false;
                }),
                switchMap((draft: AuctionFilterValue) =>
                    this.auctionService
                        .getAuctionFilters(
                            this.toApiStatuses(draft),
                            draft.type || undefined,
                            this.searchQuery,
                        )
                        .pipe(
                            catchError(() => {
                                this.clearFilterCounts();
                                return EMPTY;
                            }),
                        ),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe((res: GetAuctionFiltersResponse) =>
                this.setFilterCounts(res),
            );

        this.biddingUtils
            .getCurrentUserId()
            .subscribe((currentUserId: string) => {
                this.currentUserId = currentUserId;
            });

        // Reconnect REST backstop: refetch auction list on non-recovered reconnect.
        this.socketService.reconnected$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.getAllAuctionsServiceCall(this.pageNo));
    }

    /** The view actually rendered. Phone widths get the card view
     *  automatically; otherwise the user's grid/list choice rules. */
    get effectiveTab(): string {
        return this.isPhone ? 'grid' : this.activeTab;
    }

    handleActiveTab(tab: string): void {
        this.activeTab = tab;
    }

    get appliedFilterCount(): number {
        return activeAuctionFilterCount(this.appliedFilter);
    }

    openFilterModal(): void {
        this.filterDraft = copyAuctionFilter(this.appliedFilter);
        this.filterModalOpen = true;
        this.filterCounts$.next(this.filterDraft);
    }

    onFilterDraftChange(next: AuctionFilterValue): void {
        this.filterDraft = next;
        if (this.filterModalOpen) this.filterCounts$.next(next);
    }

    private toApiStatuses(value: AuctionFilterValue): AuctionStatus[] {
        return value.statuses.map(
            (status: AuctionFilterStatus) => API_STATUS_BY_FILTER[status],
        );
    }

    private setFilterCounts({ data }: GetAuctionFiltersResponse): void {
        const statusCounts: Partial<Record<AuctionFilterStatus, number>> = {};
        (data.statuses ?? []).forEach(
            ({ status, count }: GetAuctionFiltersResponseDataStatusesInner) => {
                const key: AuctionFilterStatus | undefined = status
                    ? FILTER_STATUS_BY_API[status]
                    : undefined;
                if (key) statusCounts[key] = count ?? 0;
            },
        );

        // The API omits zero-count types; every offered type gets a number.
        const typeCounts: Record<string, number> = Object.fromEntries(
            this.filterTypes.map(({ value }: AuctionTypeOption) => [value, 0]),
        );
        (data.auctionTypes ?? []).forEach(
            ({
                auctionType,
                count,
            }: GetAuctionFiltersResponseDataAuctionTypesInner) => {
                if (auctionType) typeCounts[auctionType] = count ?? 0;
            },
        );

        this.filterMatchCount = data.totalCount;
        this.filterStatusCounts = statusCounts;
        this.filterTypeCounts = typeCounts;
    }

    private clearFilterCounts(): void {
        this.filterMatchCount = undefined;
        this.filterStatusCounts = undefined;
        this.filterTypeCounts = undefined;
    }

    onFilterApply(): void {
        this.filterModalOpen = false;
        this.applyFilter(this.filterDraft);
    }

    onFilterClosed(): void {
        this.filterModalOpen = false;
    }

    resetFilters(): void {
        this.applyFilter(DEFAULT_AUCTION_FILTER);
    }

    private applyFilter(filter: AuctionFilterValue): void {
        this.appliedFilter = copyAuctionFilter(filter);
        this.pageNo = 1;
        // Commit state before navigating so the queryParams subscription sees
        // the type (and page) already in sync and doesn't load a second time.
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
                category: this.appliedFilter.type || null,
                page: null,
            },
            queryParamsHandling: 'merge',
        });
        this.getAllAuctionsServiceCall(1);
        this.filterCounts$.next(this.appliedFilter);
    }

    private capitalizeFirst(str: string): string {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1).toLocaleLowerCase();
    }

    // shop changeFilterSelect
    changeFilterSelect(selectedOption: { value: string; text: string }): void {
        this.sortByFilter(selectedOption.value);
    }

    public get auctionStatus(): typeof AuctionStatus {
        return AuctionStatus;
    }

    // SortBy Filter
    sortByFilter(_value: string): void {
        // this.router
        //     .navigate([], {
        //         relativeTo: this.route,
        //         queryParams: { sortBy: value ? value : null },
        //         queryParamsHandling: 'merge', // preserve the existing query params in the route
        //         skipLocationChange: false, // do trigger navigation
        //     })
        //     .finally(() => {
        //         this.viewScroller.setOffset([120, 120]);
        //         this.viewScroller.scrollToAnchor('products'); // Anchore Link
        //     });
    }

    /** A page picked in either paginator: back up to the search bar, then load. */
    onPageChange(page: number): void {
        scrollUpTo(this.searchBar?.nativeElement, !this.isMobile);
        this.setPage(page);
    }

    // product Pagination
    setPage(page: number): void {
        this.pageNo = page;
        this.getAllAuctionsServiceCall(page);
    }

    setPageSize(pageSize: number): void {
        this.pageSize = pageSize;
        this.pageNo = 1;
        this.getAllAuctionsServiceCall(1);
    }

    onSearch(query: string): void {
        this.searchQuery = query || undefined;
        this.pageNo = 1;
        this.getAllAuctionsServiceCall(1);
        this.filterCounts$.next(this.appliedFilter);
    }

    getAllAuctionsServiceCall(page: number): void {
        this.isLoading = true;
        this.unableToLoad = false;
        this.paginate = {};
        this.auctions = [];
        this.auctionService
            .getAuctions(
                page,
                this.pageSize,
                this.toApiStatuses(this.appliedFilter),
                this.appliedFilter.type || undefined,
                this.searchQuery,
            )
            .subscribe({
                next: (res: GetFeaturedAuctions200Response) => {
                    this.isLoading = false;
                    if (Array.isArray(res.data)) {
                        this.auctions = res.data.sort(
                            (
                                a: GetFeaturedAuctions200ResponseAllOfDataInner,
                                b: GetFeaturedAuctions200ResponseAllOfDataInner,
                            ) =>
                                new Date(a.toDatetime).getTime() -
                                new Date(b.toDatetime).getTime(),
                        );

                        this.filteredAuctions = this.auctions.map(
                            (
                                auction: GetFeaturedAuctions200ResponseAllOfDataInner,
                            ) => ({ ...auction, bidSnipeWindow: 0 }),
                        );

                        this.paginate = this.productService.getPager(
                            Number(res.totalCount),
                            Number(+Number(res.page)),
                            res.pageSize,
                        );
                    }
                },
                error: ({ error }: any) => {
                    const queryParams: Params = {
                        page: 1,
                        category: this.appliedFilter.type || null,
                    };
                    this.router.navigate([], {
                        relativeTo: this.route,
                        queryParams,
                        queryParamsHandling: 'merge',
                    });
                    this.isLoading = false;
                    if (error.errorCode !== 1060) {
                        this.unableToLoad = true;
                    }
                },
            });
    }

    ngOnDestroy(): void {
        this.snackbarService.dismissAll();
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Updates the earliest lot closing time based on the fetched lots data.
     *
     * @param lotsResult The result of the fetched lots data.
     */
    updateEarliestLotClosingTime(
        auction: GetFeaturedAuctions200ResponseAllOfDataInner,
    ): string {
        if (auction.toDatetime && auction.fromDatetime) {
            return (this.earliestLotClosingTime =
                new Date(auction.fromDatetime) < new Date()
                    ? auction.toDatetime
                    : auction.fromDatetime);
        }

        return '';
    }

    getAuctionText(
        auction: GetFeaturedAuctions200ResponseAllOfDataInner,
    ): string {
        return getAuctionStatusText(auction.fromDatetime, auction.toDatetime);
    }

    getAuctionStatusBannerText(
        auction: GetFeaturedAuctions200ResponseAllOfDataInner,
    ): string {
        return getAuctionStatusBannerText(auction);
    }

    getAuctionStatus(
        auction: GetFeaturedAuctions200ResponseAllOfDataInner,
    ): string {
        return getAuctionStatus(auction.fromDatetime, auction.toDatetime);
    }

    /**
     * Checks the given category against the keys from categoryToClassMap.
     * Returns the corresponding CSS class if a match is found, otherwise returns the default class for 'restaurant'.
     *
     * @param category The category to check.
     * @returns The corresponding CSS class or the default class if no matching category is found.
     */
    getCategoryClass(category: string): string {
        return getCategoryClass(category);
    }
}
